import React, { Fragment, useEffect } from "react";
import { Link } from "react-router-dom";

import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Footer from "../../../components/footer/Footer";
import Scrollbar from "../../../components/scrollbar/scrollbar";

import "./HardwarePlatformEngineering.css";
import "./PageTheme.css";
import serviceImage from "../../../images/about/chip-main.jpg";

type Block = { title?: string; text?: string; lines?: string[]; raw: string };

const config = {
  "title": "Hardware Platform Engineering",
  "category": "Software, Platforms & Infrastructure",
  "visual": "hardware",
  "folder": "HardwarePlatformEngineering",
  "sections": [
    [
      "Hardware Platform Engineering",
      "Building the Foundation for Reliable Electronic Systems",
      "Robust hardware platforms are the foundation of every successful electronic product. They enable system integration, software development, validation, and product realization by providing a stable environment for engineering, testing, and deployment. As product complexity continues to grow, well-engineered hardware platforms play a critical role in accelerating development and improving overall system reliability.\n\nDigicomm Semiconductor provides specialized Hardware Platform Engineering services that support the complete hardware platform development lifecycle. From platform architecture and board design support to prototype development, system integration, hardware bring-up, and validation, our engineering teams collaborate closely with customers to develop reliable, scalable, and application-ready hardware platforms through disciplined engineering execution and collaborative product development.\n\nPrimary CTA\n\nDiscuss Your Hardware Platform Requirements"
    ],
    [
      "Hardware Platform Engineering",
      "Hardware platforms provide the foundation for developing, validating, and deploying modern electronic products. They bring together processing, memory, power management, communication interfaces, and peripheral components into a cohesive architecture that supports software development, system integration, testing, and product validation. Well-engineered hardware platforms reduce development risk, accelerate bring-up, and create a stable environment for the entire product lifecycle.\n\nDigicomm Semiconductor delivers comprehensive Hardware Platform Engineering services that support every stage of platform development. Our engineering teams work collaboratively with customers to architect, integrate, prototype, validate, and optimize hardware platforms that enable reliable product development and seamless hardware-software integration.\n\nOur Hardware Platform Engineering Capabilities\nHardware Platform Architecture\nDevelopment Board Engineering\nReference Platform Development\nPrototype Development & Bring-up\nHardware Integration\nSystem Validation Support\nPerformance Optimization\nProduction Readiness Support"
    ],
    [
      "Why Hardware Platform Engineering Matters",
      "Why Hardware Platform Engineering Matters\nA reliable hardware platform provides the engineering foundation for the entire product development lifecycle. It enables software development, system integration, hardware validation, and performance optimization by creating a stable and scalable environment where multiple engineering disciplines can work together efficiently. As products become increasingly complex, robust hardware platforms help reduce integration challenges, accelerate development, and improve overall product quality.\n\nEffective Hardware Platform Engineering also supports faster prototyping, streamlined system bring-up, and smoother transitions from development to production. By establishing a dependable platform early in the design cycle, organizations can minimize engineering risks, improve development efficiency, and build products with greater confidence.\n\nKey Outcomes\nAccelerated Product Development\n\nEnable parallel hardware and software development through stable engineering platforms that reduce overall development timelines.\n\nSeamless System Integration\n\nSupport efficient integration of processors, memory, peripherals, interfaces, and embedded software into a unified hardware platform.\n\nReduced Development Risk\n\nIdentify and resolve platform-level challenges early, improving engineering confidence and reducing downstream rework.\n\nImproved Product Reliability\n\nDevelop stable and validated hardware platforms that contribute to consistent product performance throughout the lifecycle.\n\nFaster Prototype Bring-up\n\nCreate engineering platforms that simplify hardware verification, software enablement, and functional validation during early development.\n\nProduction Readiness\n\nEstablish scalable hardware platforms that support a smoother transition from engineering prototypes to production-ready products."
    ],
    [
      "Our Hardware Platform Engineering Capabilities",
      "Our Hardware Platform Engineering Capabilities\n\nDeveloping a reliable hardware platform requires coordinated engineering across architecture, prototyping, integration, validation, and performance optimization. Digicomm supports the complete hardware platform lifecycle, helping customers build stable, scalable, and production-ready platforms that accelerate product development and simplify system integration.\n\nHardware Platform Architecture\n\nDevelop robust platform architectures that establish the foundation for reliable system development, scalability, and long-term product evolution.\n\nDevelopment Board Engineering\n\nDesign and develop engineering platforms that support hardware evaluation, software development, system testing, and product validation.\n\nReference Platform Development\n\nBuild reference platforms that accelerate product development, technology evaluation, and application-specific customization.\n\nPrototype Development & Bring-up\n\nSupport prototype assembly, hardware bring-up, functional verification, and early-stage platform validation to accelerate engineering progress.\n\nHardware & System Integration\n\nIntegrate processors, memory, interfaces, peripherals, and embedded software into a cohesive and reliable hardware platform.\n\nPlatform Validation & Debugging\n\nValidate hardware functionality, identify platform-level issues, and support systematic debugging to improve engineering confidence.\n\nPerformance & Power Optimization\n\nOptimize platform performance, power efficiency, and resource utilization to meet product-specific operating requirements.\n\nProduction Readiness Support\n\nPrepare hardware platforms for manufacturing by improving stability, validating functionality, and supporting a smooth transition to production."
    ],
    [
      "Key Benefits of Hardware Platform Engineering",
      "Key Benefits of Hardware Platform Engineering\n\nA well-engineered hardware platform provides a stable foundation for the entire product lifecycle. By enabling efficient development, reliable integration, and systematic validation, it helps engineering teams reduce complexity, improve collaboration, and accelerate the journey from concept to production.\n\nAccelerated Product Development\n\nEnable parallel hardware and software engineering activities through stable development platforms that shorten development cycles and improve overall project efficiency.\n\nReliable System Integration\n\nSimplify the integration of processors, memory, peripherals, interfaces, and embedded software through a cohesive and validated hardware platform.\n\nReduced Engineering Risk\n\nIdentify and resolve platform-level issues early, minimizing integration challenges, reducing rework, and improving engineering confidence.\n\nScalable Platform Architecture\n\nDevelop flexible hardware platforms that support product evolution, feature expansion, and future technology requirements without extensive redesign.\n\nOptimized System Performance\n\nImprove overall platform efficiency through balanced hardware architecture, power optimization, and effective resource utilization.\n\nImproved Product Readiness\n\nCreate validated and production-ready hardware platforms that support smoother manufacturing transitions and consistent product performance."
    ],
    [
      "Technology Expertise",
      "Technology Expertise\nModern hardware platforms require expertise across processor architectures, development platforms, high-speed interfaces, power management, and system validation. Successful platform engineering depends on integrating these technologies into reliable, scalable environments that support software development, product validation, and long-term product evolution.\n\nDigicomm's Hardware Platform Engineering teams bring experience across a broad range of hardware technologies, enabling customers to accelerate platform development while maintaining engineering quality, system stability, and product reliability.\n\nProcessor Platforms\nARM Cortex Series\nRISC-V\nMicrocontrollers (MCUs)\nMicroprocessors (MPUs)\nSystem-on-Chip (SoC) Platforms\nHardware Components\nDDR Memory Interfaces\nFlash Memory\nPower Management ICs (PMICs)\nClocking Solutions\nSensors & Peripheral Devices\nConnectivity & Interfaces\nPCI Express (PCIe)\nUSB\nEthernet\nUART\nSPI\nI²C\nCAN\nGPIO\nDevelopment & Debugging\nDevelopment Boards\nEvaluation Platforms\nJTAG Debugging\nHardware Bring-up\nBoard Diagnostics\nFunctional Debugging\nValidation & Performance\nPlatform Validation\nSystem Integration Testing\nFunctional Verification\nPerformance Evaluation\nPower Optimization"
    ],
    [
      "Why Digicomm",
      "Why Digicomm\n\nReliable hardware platforms are built through disciplined engineering, thoughtful system integration, and a deep understanding of how hardware interacts with software and the broader product ecosystem. At Digicomm, we combine platform engineering expertise with collaborative execution to help customers develop scalable, high-performance hardware foundations that support efficient product development and long-term reliability.\n\nWhat Sets Us Apart\nSpecialized Platform Engineering Expertise\n\nEngineering teams experienced in developing hardware platforms that support system integration, software enablement, validation, and product realization across diverse semiconductor applications.\n\nEnd-to-End System Integration\n\nA collaborative engineering approach that brings together processors, memory, interfaces, peripherals, and embedded software into stable and reliable hardware platforms.\n\nDisciplined Engineering Execution\n\nStructured engineering methodologies focused on platform quality, validation, maintainability, and dependable product outcomes throughout the development lifecycle.\n\nFlexible Engineering Engagement\n\nScalable engagement models that integrate seamlessly with customer engineering teams, adapting to evolving technologies, project requirements, and development schedules.\n\nCommitment to Reliable Platform Delivery\n\nA continuous focus on developing production-ready hardware platforms that improve engineering efficiency, accelerate integration, and support long-term product success."
    ],
    [
      "Related Engineering Capabilities",
      "Related Specialized Engineering Capabilities\nEmbedded Software Systems\n\nDevelop intelligent software that integrates seamlessly with hardware platforms to enable reliable system functionality, real-time performance, and product readiness.\n\nLearn More →\n\nNetworking Solutions\n\nEnable secure and reliable communication between hardware platforms, connected devices, and distributed systems through robust networking technologies.\n\nLearn More →\n\nRelated Core Engineering Services\nFPGA Engineering\n\nAccelerate platform development, hardware validation, and early software enablement through FPGA-based prototyping and hardware realization.\n\nLearn More →\n\nSilicon Realization\n\nSupport coordinated engineering execution across hardware platforms, embedded software, validation, and implementation to enable successful product realization.\n\nLearn More →\n\nRelated Engineering Engagement Models\nEngineering Consulting\n\nLeverage experienced platform engineering specialists to solve complex hardware challenges and strengthen product development initiatives.\n\nLearn More →\n\nDedicated Engineering Teams\n\nExtend your engineering organization with skilled hardware platform engineers who integrate seamlessly into your development programs.\n\nLearn More →"
    ],
    [
      "Build Reliable Hardware Platforms with Confidence",
      "Build Reliable Hardware Platforms with Confidence\n\nEvery successful electronic product begins with a reliable hardware foundation. Whether you're architecting a new platform, accelerating prototype development, enabling hardware-software integration, or preparing for production, Digicomm Semiconductor provides the engineering expertise to help you build stable, scalable, and production-ready hardware platforms through disciplined execution and collaborative engineering.\n\nPrimary CTA\n\nDiscuss Your Hardware Platform Requirements"
    ]
  ]
} as const;

const splitBlocks = (raw: string): string[] =>
  raw.split(/\n\s*\n/g).map((x) => x.trim()).filter(Boolean);

const asBlock = (raw: string): Block => {
  const lines = raw.split("\n").map((x) => x.trim()).filter(Boolean);
  if (lines.length >= 2 && lines[0].length <= 74) {
    const rest = lines.slice(1);
    const listLike = rest.length >= 3 && rest.every((x) => x.length < 78);
    if (listLike) return { title: lines[0], lines: rest, raw };
    return { title: lines[0], text: rest.join(" "), raw };
  }
  if (lines.length === 1) return { text: lines[0], raw };
  return { text: lines.join(" "), raw };
};

const getHero = (section: readonly string[], pageTitle: string) => {
  const merged = section.join("\n\n");
  const blocks = splitBlocks(merged);
  const filtered = blocks.filter((x) => x !== "Primary CTA");
  if (filtered[0] === pageTitle) filtered.shift();

  const first = filtered.shift() || pageTitle;
  const firstLines = first.split("\n").map((x) => x.trim()).filter(Boolean);
  const headline = firstLines[0] || pageTitle;
  const body: string[] = [];

  if (firstLines.length > 1) body.push(firstLines.slice(1).join(" "));
  filtered.forEach((b) => body.push(b));

  let cta = "";
  if (body.length && body[body.length - 1].length < 85) cta = body.pop() || "";
  return { headline, body, cta };
};

const Arrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h13M13 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EngineeringVisual: React.FC = () => (
  <div className="ses-visual service-engineering-image">
    <img src={serviceImage} alt="Semiconductor hardware platform" />
  </div>
);

const ContentPanel: React.FC<{ raw: string; index: number }> = ({ raw, index }) => {
  const blocks = splitBlocks(raw).filter((x) => x !== "Primary CTA");
  return (
    <div className="ses-block-grid">
      {blocks.map((rawBlock, i) => {
        const block = asBlock(rawBlock);
        const long = rawBlock.length > 430;
        const labelOnly =
          !block.title &&
          !!block.text &&
          block.text.length < 80 &&
          (/[:：]$/.test(block.text) || i === 0);

        return (
          <article
            className={`ses-block ${long ? "ses-block--wide" : ""} ${labelOnly ? "ses-block--label" : ""}`}
            key={`${index}-${i}`}
          >
            {block.title && <h3>{block.title}</h3>}
            {block.text && (labelOnly ? <h3>{block.text}</h3> : <p>{block.text}</p>)}
            {block.lines && <ul>{block.lines.map((line) => <li key={line}>{line}</li>)}</ul>}
          </article>
        );
      })}
    </div>
  );
};

const RelatedPanel: React.FC<{ raw: string }> = ({ raw }) => {
  const blocks = splitBlocks(raw);
  const routeMap: Record<string, string> = {
    "Analog & Mixed Signal Design & Layout": "/services/analog-mixed-signal",
    "Analog & Mixed Signal": "/services/analog-mixed-signal",
    "Static Timing Analysis (STA)": "/services/static-timing-analysis",
    "FPGA Engineering": "/services/fpga-engineering",
    "Embedded Software Systems": "/services/embedded-software-systems",
    "Hardware Platform Engineering": "/services/hardware-platform-engineering",
    "Networking Solutions": "/services/networking-solutions",
    "Silicon Validation": "/services/silicon-validation",
    "EDA & Design Automation": "/services/eda-design-automation",
    "Product Engineering": "/services/product-engineering",
    "ASIC & SoC Design": "/services/asic-soc-design",
    "Physical Design": "/services/physical-design",
    "Verification Services": "/services/verification",
  };

  const cards: React.ReactNode[] = [];
  let group = "";

  blocks.forEach((b, i) => {
    if (/^Related .*:$/.test(b.trim())) {
      group = b.trim();
      return;
    }
    if (b === "Learn More →") return;

    const lines = b.split("\n").map((x) => x.trim()).filter(Boolean);
    const title = lines[0];
    const desc = lines.slice(1).join(" ");

    if (routeMap[title]) {
      cards.push(
        <Link className="ses-related-card" to={routeMap[title]} key={`${title}-${i}`}>
          <span>{group}</span>
          <h3>{title}</h3>
          {desc && <p>{desc}</p>}
          <em>Learn More <Arrow/></em>
        </Link>
      );
    } else {
      cards.push(
        <article className="ses-related-card" key={`${title}-${i}`}>
          <span>{group}</span>
          <h3>{title}</h3>
          {desc && <p>{desc}</p>}
        </article>
      );
    }
  });

  return <div className="ses-related-grid">{cards}</div>;
};

const Page: React.FC = () => {
  const sections = config.sections as readonly (readonly string[])[];
  const hero = getHero(sections[0] || [], config.title);

  const lastSection = sections[sections.length - 1] || [];
  const lastRaw = lastSection.join("\n\n");
  const lastBlocks = splitBlocks(lastRaw).filter((x) => x !== "Primary CTA");
  const ctaLabel =
    lastBlocks.length && lastBlocks[lastBlocks.length - 1].length < 90
      ? lastBlocks[lastBlocks.length - 1]
      : "";
  const ctaContent = ctaLabel ? lastBlocks.slice(0, -1) : lastBlocks;
  const hasRelated = sections.length === 9;

  useEffect(() => {
    document.title = `${config.title} | Digicomm Semiconductor`;
  }, []);

  return (
    <Fragment>
      <div className={`specialized-service-page inner-page hardware-redesign`}>
        <div className="body_wrap o-clip">
          <Header />

          <main>
            <PageTitle pageTitle={config.title} pagesub={config.title} />

            <section className="ses-hero">
              <div className="ses-shell ses-two-col">
                <div className="ses-hero__copy">
                  <h1>{hero.headline}</h1>
                  {hero.body.map((p, i) => <p key={i}>{p}</p>)}
                  {hero.cta && (
                    <Link className="ses-btn" to="/contact">
                      {hero.cta}<Arrow/>
                    </Link>
                  )}
                </div>
                <EngineeringVisual />
              </div>
            </section>

            {sections.slice(1, hasRelated ? -2 : -1).map((section, idx) => {
              const raw = section.join("\n\n");
              const blocks = splitBlocks(raw);
              const lead = blocks[0] || "";
              const firstLine = lead.split("\n")[0] || "";

              return (
                <section className={`ses-section ses-section--${idx + 2} ${idx % 2 ? "ses-section--alt" : ""}`} key={idx}>
                  <div className="ses-shell">
                    <div className="ses-section-head">
                      <span>{String(idx + 2).padStart(2, "0")}</span>
                      <h2>{firstLine}</h2>
                    </div>
                    <ContentPanel raw={raw} index={idx}/>
                  </div>
                </section>
              );
            })}

            {hasRelated && (
              <section className="ses-section ses-related">
                <div className="ses-shell">
                  <RelatedPanel raw={sections[sections.length - 2].join("\n\n")}/>
                </div>
              </section>
            )}

            <section className="ses-cta">
              <div className="ses-shell ses-two-col">
                <div className="ses-cta__copy">
                  {ctaContent.map((b, i) => {
                    const lines = b.split("\n").map((x) => x.trim()).filter(Boolean);
                    return i === 0
                      ? <h2 key={i}>{lines.join(" ")}</h2>
                      : <p key={i}>{lines.join(" ")}</p>;
                  })}

                  {ctaLabel && (
                    <Link className="ses-btn" to="/contact">
                      {ctaLabel}<Arrow/>
                    </Link>
                  )}
                </div>
                <EngineeringVisual />
              </div>
            </section>
          </main>

          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default Page;

